import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getNiche, toggleBookmark } from "@/lib/api";
import ScoreRadar from "@/components/ScoreRadar";
import {
  VideoRunRatePopover,
  NicheRunRatePopover,
} from "@/components/RunRatePopover";
import {
  estimateMonthlyRunRate,
  estimateNicheMonthly,
  estimateRPM,
  estimateVideoLifetimeRevenue,
  fmtMoney,
  isYPPEligible,
} from "@/lib/earnings";
import { toast } from "sonner";

const PILLAR_LABELS = {
  views_to_sub: "Views / Sub",
  faceless_potential: "Faceless Potential",
  evergreen_value: "Evergreen Value",
  monetization_potential: "Monetization",
  production_difficulty: "Easy Production",
};

const PLAYBOOK_FIELDS = [
  ["format", "Format"],
  ["posting_cadence", "Cadence"],
  ["faceless_strategy", "Faceless strategy"],
];

function fmtViews(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n);
}

function fmtPublished(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 14) return `${days}d ago`;
  if (days < 60) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function NicheDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [niche, setNiche] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getNiche(id);
        setNiche(data);
      } catch {
        toast.error("Niche not found");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="px-6 py-10 font-mono text-sm text-zinc-500">
        [recon] decrypting dossier<span className="blink">_</span>
      </div>
    );
  }
  if (!niche) return null;

  const handleBookmark = async () => {
    setSaving(true);
    try {
      const res = await toggleBookmark(niche.niche_id);
      setNiche((n) => ({ ...n, bookmarked: res.bookmarked }));
      toast.success(res.bookmarked ? "Saved to your dossier" : "Removed from saved");
    } catch {
      toast.error("Could not update bookmark");
    } finally {
      setSaving(false);
    }
  };

  const score = niche.scores?.composite_score ?? 0;
  const monetizationScore = niche.scores?.monetization_potential ?? 5;
  const rpm = estimateRPM(monetizationScore);
  const totalMonthly = estimateNicheMonthly(niche);
  const totalLifetime = niche.videos.reduce(
    (s, v) => s + estimateVideoLifetimeRevenue(v.views, monetizationScore),
    0,
  );

  return (
    <div data-testid="niche-detail-page" className="px-6 lg:px-10 py-8 max-w-[1500px] mx-auto">
      <button
        data-testid="back-btn"
        onClick={() => navigate(-1)}
        className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-yellow-400 transition-colors mb-6"
      >
        ◀ Back to dossier
      </button>

      <header className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-10">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
            // NICHE DOSSIER · CLASSIFIED
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[0.95] mb-4 max-w-3xl">
            {niche.name}
          </h1>
          <p className="text-zinc-400 max-w-2xl text-base leading-relaxed">
            {niche.thesis}
          </p>
        </div>
        <div className="flex flex-col items-end gap-3 shrink-0">
          <div
            data-testid="composite-score"
            className="border border-yellow-400 px-5 py-4 rounded-sm text-center"
            style={{ boxShadow: "0 0 0 4px rgba(250,204,21,0.08)" }}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-1">
              OPP SCORE
            </div>
            <div className="font-mono font-black text-5xl text-yellow-400 leading-none">
              {score}
            </div>
          </div>
          <button
            data-testid="bookmark-btn"
            onClick={handleBookmark}
            disabled={saving}
            className={`font-mono text-xs uppercase tracking-wider px-4 py-2 rounded-sm border transition-colors w-full ${
              niche.bookmarked
                ? "border-yellow-400 text-yellow-400 bg-yellow-400/10"
                : "border-zinc-700 text-zinc-300 hover:border-yellow-400 hover:text-yellow-400"
            }`}
          >
            {niche.bookmarked ? "★ Saved" : "☆ Save niche"}
          </button>
          {niche.bookmarked && (
            <button
              data-testid="open-channel-kit-btn"
              onClick={() => navigate(`/niches/${niche.niche_id}/channel-kit`)}
              className="font-mono text-xs uppercase tracking-wider px-4 py-2 rounded-sm border border-yellow-400/60 text-yellow-400 hover:bg-yellow-400/10 w-full transition-colors"
              title="Generate channel name, banner, avatar, About page and first script"
            >
              ▸ Build channel kit
            </button>
          )}
        </div>
      </header>

      {/* Score grid */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-12">
        <div className="lg:col-span-2 bg-zinc-950 border border-zinc-800 rounded-sm p-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
            5-Pillar Profile
          </div>
          <ScoreRadar scores={niche.scores} />
        </div>
        <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800 rounded-sm overflow-hidden">
          {Object.entries(PILLAR_LABELS).map(([k, label]) => (
            <div key={k} className="bg-black p-5">
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                {label}
              </div>
              <div className="flex items-end gap-2">
                <span className="font-mono font-black text-4xl text-yellow-400 leading-none">
                  {niche.scores?.[k] ?? 0}
                </span>
                <span className="text-zinc-600 text-xs font-mono">/ 10</span>
              </div>
              <div className="mt-3 h-1 bg-zinc-900">
                <div
                  className="h-full bg-yellow-400"
                  style={{ width: `${(niche.scores?.[k] || 0) * 10}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Earnings estimate strip */}
      <section
        data-testid="earnings-strip"
        className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mb-12 rounded-sm overflow-hidden"
      >
        <div className="bg-black p-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
            Est. monthly run-rate
          </div>
          <NicheRunRatePopover
            niche={niche}
            total={totalMonthly}
            lifetime={totalLifetime}
            rpm={rpm}
          >
            <div
              data-testid="niche-monthly-total"
              className="font-mono font-black text-3xl text-yellow-400 tracking-tight"
            >
              {fmtMoney(totalMonthly)}
              <span className="text-zinc-500 text-sm font-normal ml-1">/ mo</span>
            </div>
          </NicheRunRatePopover>
        </div>
        <div className="bg-black p-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
            Est. lifetime earnings
          </div>
          <div className="font-mono font-black text-3xl text-yellow-400 tracking-tight">
            {fmtMoney(totalLifetime)}
          </div>
        </div>
        <div className="bg-black p-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
            Assumed RPM
          </div>
          <div className="font-mono font-black text-3xl text-yellow-400 tracking-tight">
            ${rpm}
            <span className="text-zinc-500 text-sm font-normal ml-1">/ 1k</span>
          </div>
        </div>
        <div className="bg-black p-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
            Method
          </div>
          <div className="text-zinc-300 text-xs leading-snug">
            views × RPM, scaled to 30-day run-rate. Channels &lt; 1k subs are
            pre-monetization — figures are potential.
          </div>
        </div>
      </section>

      {/* Breakout videos */}
      <section className="mb-12">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-2xl font-bold text-white">Breakout signals</h2>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
            {niche.videos?.length} videos
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {niche.videos.map((v) => {
            const lifetime = estimateVideoLifetimeRevenue(v.views, monetizationScore);
            const monthly = estimateMonthlyRunRate(v, monetizationScore);
            const eligible = isYPPEligible(v.subscribers);
            return (
              <div
                key={v.video_id}
                data-testid={`breakout-video-${v.video_id}`}
                className="bg-zinc-950 border border-zinc-800 hover:border-zinc-600 rounded-sm overflow-hidden transition-colors flex flex-col"
              >
                <a
                  href={`https://www.youtube.com/watch?v=${v.video_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="relative aspect-video bg-zinc-900 block group/thumb"
                  title="Open on YouTube"
                >
                  <img
                    src={v.thumbnail}
                    alt={v.title}
                    className="w-full h-full object-cover opacity-90 group-hover/thumb:opacity-100 transition-opacity"
                    loading="lazy"
                  />
                  <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 font-mono text-[11px] text-yellow-400">
                    {Math.round(v.breakout_multiplier)}× breakout
                  </div>
                  <div className="absolute top-2 left-2 bg-black/80 px-2 py-1 font-mono text-[11px] text-white">
                    {fmtMoney(lifetime)} est.
                  </div>
                </a>
                <div className="p-4 flex-1 flex flex-col">
                  <a
                    href={`https://www.youtube.com/watch?v=${v.video_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-white font-semibold leading-snug mb-2 line-clamp-2 hover:text-yellow-400 transition-colors"
                  >
                    {v.title}
                  </a>
                  <div className="flex items-center justify-between font-mono text-[11px] text-zinc-500 mb-1">
                    <span className="truncate max-w-[55%]">{v.channel_name}</span>
                    <span className="text-zinc-400">
                      {fmtViews(v.views)} views · {fmtViews(v.subscribers)} subs
                    </span>
                  </div>
                  <div
                    data-testid={`video-published-${v.video_id}`}
                    className="font-mono text-[11px] text-zinc-500 mb-2"
                  >
                    Posted {fmtPublished(v.published_at)}
                  </div>
                  <div className="flex items-center justify-between font-mono text-[11px] mt-auto">
                    <VideoRunRatePopover
                      video={v}
                      monetizationScore={monetizationScore}
                      eligible={eligible}
                      label={
                        eligible
                          ? `${fmtMoney(monthly)}/mo run-rate`
                          : `${fmtMoney(monthly)}/mo potential`
                      }
                    />
                    <span
                      className={`px-1.5 py-0.5 uppercase tracking-wider border rounded-sm text-[10px] ${
                        eligible
                          ? "border-green-500/50 text-green-400"
                          : "border-zinc-700 text-zinc-500"
                      }`}
                    >
                      {eligible ? "Monetized" : "Pre-YPP"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Content ideas + Playbook */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-16">
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 mb-4">
            // ORIGINAL CONTENT IDEAS
          </div>
          <ol className="space-y-3" data-testid="content-ideas-list">
            {niche.content_ideas.map((idea, i) => (
              <li
                key={i}
                className="flex gap-4 border-l-2 border-yellow-400/50 pl-4 py-1"
              >
                <span className="font-mono text-xs text-yellow-400 pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-zinc-200 text-sm leading-relaxed">
                  {idea}
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div
          data-testid="creator-playbook"
          className="bg-zinc-950 border border-zinc-800 rounded-sm p-6"
        >
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 mb-4">
            // CREATOR PLAYBOOK
          </div>
          <div className="space-y-5">
            {PLAYBOOK_FIELDS.map(([k, label]) => (
              <div key={k}>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                  {label}
                </div>
                <div className="text-sm text-zinc-200 leading-relaxed">
                  {niche.creator_playbook[k]}
                </div>
              </div>
            ))}

            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                Tools
              </div>
              <div className="flex flex-wrap gap-2">
                {niche.creator_playbook.tools.map((t, i) => (
                  <span
                    key={i}
                    className="font-mono text-[11px] uppercase tracking-wider px-2 py-1 border border-zinc-800 text-zinc-300 rounded-sm"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                Monetization
              </div>
              <ul className="space-y-1 text-sm text-zinc-200">
                {niche.creator_playbook.monetization.map((m, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-yellow-400">$</span>
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                First 30 days
              </div>
              <ol className="space-y-1 text-sm text-zinc-200 font-mono">
                {niche.creator_playbook.first_30_days.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-yellow-400">▸</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
